import React, { memo, useState } from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TableSortLabel,
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery
} from "@mui/material";
import TruncatedText from "../Truncate/TruncatedText";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export interface RDTColumn {
  name: string | React.ReactNode;
  selector?: (row: any) => React.ReactNode;
  width?: string;
  sortable?: boolean;
  onCellClick?: (row: any) => void;
  customRender?: (row: any, rowIndex?: number) => React.ReactNode;
  tooltip?: (row: any) => string;
  align?: "left" | "center" | "right";
  sortKey?: string;
  truncateLimit?: number;
}

interface TableProps {
  columns: RDTColumn[];
  data: any[];
  className?: string;
  isTableBodyScroll?: boolean;
  maxHeight?: string;
  paginationProps?: {
    isPagination: boolean;
    totalCount: number;
    pageSelected: number;
    setPageSelected: (page: number) => void;
    rowsPerPageValue: number;
    setRowsPerPageValue: (rows: number) => void;
  };
  noDataText?: string;
  showCheckbox?: boolean;
  checkboxWidth?: string;
  onSelectionChange?: (ids: any[], isSelectAll?: boolean) => void;
  selectedRows?: any[];
  showExpandedRow?: boolean;
  loading?: boolean;
  onSortChange?: (sortBy: string) => void;
  tableWidth?: string;
  expandedRowRender?: (row: any, rowIndex: number, columns: RDTColumn[]) => React.ReactNode;
  isRowExpandable?: (row: any) => boolean;
  rowKey?: string;
  getRowStyle?: (row: any) => React.CSSProperties;
  showRegularizedRow?: boolean;
  regularizedRowRender?: (row: any, rowIndex: number) => React.ReactNode;
  isGrid?: boolean;
  rowHeight?: number;
  isTileHeader?: boolean;
  sortState?: Record<string, "asc" | "desc">;
  setSortState?: React.Dispatch<React.SetStateAction<Record<string, "asc" | "desc">>>;
  truncateCellContent?: boolean;
  headerFontWeight?: React.CSSProperties["fontWeight"];
  headerFontSize?: React.CSSProperties["fontSize"];
  cellPadding?: React.CSSProperties["padding"];
  defaultTruncateLimit?: number;
  checkboxCondition?: (row: any) => boolean;
  /** Body cell vertical alignment. Use "top" only for stacked custom content (e.g. approver chips). */
  bodyVerticalAlign?: "middle" | "top" | "bottom";
  selectAllCheckbox?: boolean; // select all checkbox across pages
  forceSelectAll?: boolean;
}

const CHECKBOX_COL_WIDTH = 30;
const HEADER_BG = "#f4f6f9";
const HEADER_BORDER = "1px solid #d0d5dd";
const ROW_BORDER = "1px solid #e8eaed";

const checkboxCellSx = {
  width: CHECKBOX_COL_WIDTH,
  minWidth: CHECKBOX_COL_WIDTH,
  maxWidth: CHECKBOX_COL_WIDTH,
  padding: "12px 8px",
  verticalAlign: "middle" as const,
  boxSizing: "border-box" as const,
  borderBottom: ROW_BORDER,
};

const stickyHeaderSx = {
  // backgroundColor: HEADER_BG,
  position: "sticky" as const,
  top: 0,
  borderBottom: HEADER_BORDER,
};

const sortLabelSx = (headerFontWeight: React.CSSProperties["fontWeight"]) => ({
  fontWeight: headerFontWeight,
  color: "inherit",
  "&.Mui-active": { color: "inherit" },
  "& .MuiTableSortLabel-icon": { opacity: 0 },
  "&:hover .MuiTableSortLabel-icon": { opacity: 0.5 },
  "&.Mui-active .MuiTableSortLabel-icon": { opacity: 1 },
});

/** Shared header band height for side-by-side split tables (isGrid). */
const GRID_HEADER_HEIGHT = 56;

const gridHeaderRowSx = {
  height: GRID_HEADER_HEIGHT,
};

const gridHeaderCellSx = {
  height: GRID_HEADER_HEIGHT,
  maxHeight: GRID_HEADER_HEIGHT,
  padding: "8px 10px",
  boxSizing: "border-box" as const,
  verticalAlign: "middle" as const,
  overflow: "hidden",
  whiteSpace: "nowrap" as const,
  wordBreak: "normal" as const,
};

const gridTileHeaderBoxSx = {
  width: "100%",
  height: "100%",
  maxHeight: "100%",
  padding: "4px 6px",
  backgroundColor: "#f0f0f0",
  borderRadius: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  border: "1px solid #ddd",
  fontWeight: 600,
  boxSizing: "border-box" as const,
  overflow: "hidden",
};

const Table: React.FC<TableProps> = ({
  columns,
  data,
  className,
  isTableBodyScroll = true,
  maxHeight,
  paginationProps,
  noDataText = "No Records Found",
  showCheckbox = false,
  checkboxWidth = "5px",
  onSelectionChange,
  selectedRows,
  showExpandedRow,
  loading = false,
  onSortChange,
  tableWidth,
  expandedRowRender,
  isRowExpandable = () => true,
  rowKey = "Id",
  getRowStyle,
  showRegularizedRow,
  regularizedRowRender,
  isGrid = false,
  rowHeight = 56,
  isTileHeader = false,
  sortState = {},
  setSortState = () => { },
  truncateCellContent = true,
  headerFontWeight = 700,
  headerFontSize = "0.875rem",
  cellPadding,
  defaultTruncateLimit = 10,
  checkboxCondition,
  bodyVerticalAlign = "middle",
  selectAllCheckbox = false,
  forceSelectAll = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [expandedRowId, setExpandedRowId] = useState<any>(null);

  const selected = selectedRows || [];

  const getRowId = (row: any, index?: number) => row?.[rowKey] ?? `row-${index}`;

  const selectableRows = checkboxCondition
    ? data.filter((row) => checkboxCondition(row))
    : data;

  const toggleSelectAll = (checked: boolean) => {
    if (selectAllCheckbox) {
      if (forceSelectAll && checked === false) {
        onSelectionChange?.([], false);
        return;
      }

      onSelectionChange?.([], checked);
      return;
    }

    const ids = checked ? selectableRows.map((r, i) => getRowId(r, i)) : [];
    onSelectionChange?.(ids, checked);
  };

  const toggleRow = (id: any) => {
    const newSelected = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];

    onSelectionChange?.(newSelected);
  };

  const isAllSelected = forceSelectAll
    ? true
    : (
      selectableRows.length > 0 &&
      selectableRows.every((row, index) =>
        selected.includes(getRowId(row, index))
      )
    );

  const isPartial = !forceSelectAll &&
    selected.length > 0 &&
    selected.length < selectableRows.length;

  const toggleExpand = (row: any, index: number) => {
    if (!isRowExpandable?.(row)) return;

    const id = getRowId(row, index);

    setExpandedRowId((prev: any) =>
      prev === id ? null : id
    );
  };

  // Sorting logic
  const handleSort = (col: RDTColumn) => {
    if (!col.sortKey) return;

    const key = col.sortKey;

    const next = sortState[key] === "asc" ? "desc" : "asc";

    const updated: Record<string, "asc" | "desc"> = {
      ...sortState,
      [key]: next,
    };

    setSortState?.(updated);
    onSortChange?.(`${key}${next.toUpperCase()}`);
  };

  const extraColumns = (showCheckbox ? 1 : 0) + (showExpandedRow ? 1 : 0);

  return (
    <Box sx={{ width: "100%" }} className={className}>
      <TableContainer
        sx={{
          width: "100%",
          maxHeight: isGrid ? "unset" : isTableBodyScroll ? (maxHeight || "65vh") : "unset",
          overflowY: isGrid ? "visible" : isTableBodyScroll ? "auto" : "visible",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          borderRight: isGrid ? "1px solid #e0e0e0" : undefined,
        }}
      >
        <MuiTable
          stickyHeader
          sx={{
            tableLayout: isMobile ? "auto" : "fixed",
            minWidth: isMobile ? "max-content" : "100%",
            width: tableWidth || "100%",
            borderCollapse: "collapse",
          }}
        >
          {/* Table Head */}
          <TableHead>
            <TableRow sx={isGrid ? gridHeaderRowSx : undefined}>
              {showCheckbox && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    ...checkboxCellSx,
                    ...stickyHeaderSx,
                    ...(isGrid ? gridHeaderCellSx : {}),
                    zIndex: 3,
                    width: checkboxWidth,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isPartial}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                </TableCell>
              )}
              {columns.map((col, index) => (
                <TableCell
                  key={index}
                  align={col.align || "left"}
                  sx={{
                    width: col.width || "auto",
                    fontWeight: headerFontWeight,
                    fontSize: headerFontSize,
                    color: "rgba(0, 0, 0, 0.87)",
                    lineHeight: 1.35,
                    ...(!isGrid
                      ? { whiteSpace: "normal", wordBreak: "break-word" }
                      : {}),
                    ...stickyHeaderSx,
                    ...(isGrid ? gridHeaderCellSx : {}),
                    zIndex: 2,
                    ...(!isGrid && cellPadding != null ? { padding: cellPadding } : {}),
                    ...(col.sortable && col.sortKey
                      ? {
                        cursor: "pointer",
                        "&:hover .MuiTableSortLabel-icon": { opacity: 0.5 },
                      }
                      : {}),
                  }}
                >
                  {isTileHeader ? (
                    <Box
                      sx={
                        isGrid
                          ? gridTileHeaderBoxSx
                          : {
                            width: "100%",
                            padding: "6px 10px",
                            backgroundColor: "#f0f0f0",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                            border: "1px solid #ddd",
                            fontWeight: 600,
                          }
                      }
                    >
                      {col.name}
                    </Box>
                  ) : col.sortable && col.sortKey ? (
                    <TableSortLabel
                      active={!!sortState[col.sortKey]}
                      direction={sortState[col.sortKey] || "asc"}
                      onClick={() => handleSort(col)}
                      sx={sortLabelSx(headerFontWeight)}
                    >
                      {col.name}
                    </TableSortLabel>
                  ) : (
                    col.name
                  )}
                </TableCell>
              ))}

              {showExpandedRow && (
                <TableCell
                  sx={{
                    width: 50,
                    ...stickyHeaderSx,
                    zIndex: 3,
                    padding: cellPadding,
                  }}
                />
              )}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + extraColumns}
                  align="center"
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : data?.length > 0 ? (
              data.map((row, rowIndex) => {
                const rowId = getRowId(row, rowIndex);

                return (
                  <React.Fragment key={rowId}>
                    <TableRow
                      hover={false}
                      sx={{
                        height: isGrid ? rowHeight : "auto",
                        ...(getRowStyle ? getRowStyle(row) : {}),
                      }}
                    >
                      {showCheckbox && (
                        <TableCell padding="checkbox" sx={checkboxCellSx}>
                          {checkboxCondition ? (
                            checkboxCondition(row) && (
                              <Checkbox
                                size="small"
                                checked={forceSelectAll ? !selected.includes(rowId) : selected.includes(rowId)}
                                onChange={() => toggleRow(rowId)}
                                sx={{ p: 0.5 }}
                              />
                            )
                          ) : (
                            <Checkbox
                              size="small"
                              checked={forceSelectAll ? !selected.includes(rowId) : selected.includes(rowId)}
                              onChange={() => toggleRow(rowId)}
                              sx={{ p: 0.5 }}
                            />
                          )}
                        </TableCell>
                      )}

                      {columns.map((col, colIndex) => {
                        const content =
                          col.customRender?.(row, rowIndex) ??
                          col.selector?.(row) ??
                          "-";

                        const isCustomCell = typeof content !== "string";
                        const isClickable = Boolean(col.onCellClick);

                        return (
                          <TableCell
                            key={colIndex}
                            align={col.align || "left"}
                            sx={{
                              cursor: isClickable ? "pointer" : "default",
                              color: isClickable ? "#5867dd" : "inherit",
                              width: col.width || "auto",
                              minWidth: col.width || 120,
                              verticalAlign: bodyVerticalAlign,
                              ...(isCustomCell ? { overflow: "visible" } : {}),
                              ...(isClickable && isCustomCell
                                ? { "& span": { cursor: "pointer" } }
                                : {}),
                              borderBottom:
                                row.IsModifiedByAdmin && showRegularizedRow
                                  ? "none"
                                  : undefined,
                              padding: cellPadding,
                            }}
                            onClick={() => col.onCellClick?.(row)}
                          >
                            {typeof content === "string" && truncateCellContent ? (
                              <TruncatedText
                                value={content}
                                isPointer={col.onCellClick ? true : false}
                                limit={
                                  col.truncateLimit !== undefined
                                    ? col.truncateLimit
                                    : isMobile
                                      ? 6
                                      : defaultTruncateLimit
                                }
                              />
                            ) : (
                              content
                            )}
                          </TableCell>
                        );
                      })}

                      {/* Arrow icon cell */}
                      {showExpandedRow && (
                        <TableCell>
                          {isRowExpandable(row) && (
                            <IconButton onClick={() => toggleExpand(row, rowIndex)}>
                              {expandedRowId === rowId ? (
                                <KeyboardArrowDownIcon />
                              ) : (
                                <KeyboardArrowRightIcon />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>

                    {/* Expanded Row */}
                    {showExpandedRow && expandedRowId === rowId && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length + extraColumns}
                          sx={{ p: 0, minWidth: "100%" }}
                        >
                          <Collapse in timeout="auto" unmountOnExit>
                            <Box sx={{ minWidth: "max-content" }}>
                              {expandedRowRender?.(row, rowIndex, columns)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* show regularized row */}
                    {showRegularizedRow && row.IsModifiedByAdmin && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length + extraColumns}
                          sx={{
                            pt: 0,
                            minWidth: "100%",
                            color: "#09f620",
                            fontSize: "1.1rem",
                          }}
                        >
                          {regularizedRowRender ? (
                            regularizedRowRender(row, rowIndex)
                          ) : (
                            `Regularized by ${row.ModifiedBy}`
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + extraColumns} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    {noDataText}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {/* Pagination */}
      {paginationProps?.isPagination && (
        <TablePagination
          labelRowsPerPage="Items per page:"
          labelDisplayedRows={({ from, to, count }) => `${from} - ${to} of ${count !== -1 ? count : `more than ${to}`}`}
          showFirstButton
          showLastButton
          rowsPerPageOptions={[10, 30, 50, 100]}
          component="div"
          count={paginationProps.totalCount}
          rowsPerPage={paginationProps.rowsPerPageValue}
          page={paginationProps.pageSelected - 1}
          onPageChange={(_, newPage) =>
            paginationProps.setPageSelected(newPage + 1)
          }
          onRowsPerPageChange={(e) => {
            const val = parseInt(e.target.value);
            paginationProps.setRowsPerPageValue(val);
            paginationProps.setPageSelected(1);
          }}
        />
      )}
    </Box>
  );
};

export default memo(Table);
